/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';

import useItems from '../../hooks/useItems';
import { useIntersectionObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';

import { useApi } from '../../contexts/ApiContext';
import CardsList from './CardsList';

import styles from '../../../styles/lists/film-grid.module.scss';

const propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    link: PropTypes.oneOfType([AppPropTypes.link, PropTypes.string]),
    items: PropTypes.arrayOf(PropTypes.oneOfType([AppPropTypes.film])),
    gridColumns: PropTypes.number,
    query: PropTypes.shape({}),
    theme: PropTypes.oneOf(['default', 'bundle']),
    className: PropTypes.string,
    cardProps: PropTypes.shape({}),
    lazyLoad: PropTypes.bool,
    isDark: PropTypes.bool,
    hideOnEmpty: PropTypes.bool,
    count: PropTypes.number,
    onLoad: PropTypes.func,
};

const defaultProps = {
    title: null,
    description: null,
    link: null,
    items: null,
    gridColumns: 0,
    query: null,
    className: null,
    cardProps: null,
    lazyLoad: false,
    theme: 'default',
    isDark: false,
    hideOnEmpty: false,
    onLoad: null,
    count: 10,
};

const FilmCarousel = ({
    title,
    description,
    link,
    items: initialItems,
    gridColumns,
    query,
    className,
    theme,
    isDark,
    hideOnEmpty,
    lazyLoad,
    onLoad,
    count,
}) => {
    const api = useApi();
    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-10%' });

    const [wasVisible, setWasVisible] = useState(isIntersecting);

    const [hidden, setHidden] = useState(false);
    const shouldLoad = !lazyLoad || wasVisible;

    const getItems = useMemo(
        () =>
            shouldLoad
                ? () => {
                      const { related = null, ...otherQuery } = query || {};
                      return related !== null
                          ? api.films.getRelated(related, otherQuery, count)
                          : api.films
                                .get({
                                    ...query,
                                    paginated: false,
                                    count,
                                })
                                .then((response) => {
                                    const { pages = [] } = response || {};
                                    const [currentPage = null] = pages || [];
                                    return currentPage || null;
                                });
                  }
                : null,
        [api, query, shouldLoad, count],
    );

    const { items } = useItems(getItems, {
        items: initialItems,
    });

    useEffect(() => {
        if (lazyLoad && isIntersecting && !wasVisible) {
            setWasVisible(true);
        }
    }, [lazyLoad, isIntersecting, wasVisible, setWasVisible]);

    useEffect(() => {
        if (onLoad !== null) {
            onLoad(items);
        }
    }, [items, onLoad]);

    useEffect(() => {
        if (hideOnEmpty) {
            setHidden(true);
        }
    }, [items, setHidden, hideOnEmpty]);

    return (
        <CardsList
            className={classNames(styles.container, styles[theme], {
                [className]: className !== null,
                [styles.hidden]: hidden,
            })}
            title={title}
            description={description}
            link={link}
            items={
                items ||
                [...Array(10).keys()].map((id) => ({
                    id: `empty-${id}`,
                    // type: 'film',
                    title: (
                        <span
                            style={{
                                display: 'block',
                                height: '1em',
                            }}
                        />
                    ),
                }))
            }
            isGrid
            gridColumns={gridColumns}
            withMetadata
            withRef
            isDark={isDark}
            ref={ref}
        />
    );
};

FilmCarousel.propTypes = propTypes;
FilmCarousel.defaultProps = defaultProps;

export default FilmCarousel;
